import { environment } from '@environments/environment';

/** Served from the Smart Widget origin by every build (`src/assets/friendly-captcha/`). */
export const FRIENDLY_CAPTCHA_FRAME_URL = `${environment.deployUrl}assets/friendly-captcha/frame.html`;

/** Kept in sync with `src/assets/friendly-captcha/frame.js`. */
export const FRIENDLY_CAPTCHA_FRAME_SOURCE = 'calenso-friendly-captcha-frame';
export const FRIENDLY_CAPTCHA_HOST_SOURCE = 'calenso-friendly-captcha-host';

export interface FriendlyCaptchaFrameOptions {
  /** The element the captcha frame is mounted under. */
  element: HTMLElement;
  sitekey: string;
  /** Two-letter language code, as passed to the SDK. */
  language: string;
  onComplete: (response: string) => void;
  onError: (error: unknown) => void;
  /** Overridable for tests; defaults to FRIENDLY_CAPTCHA_FRAME_URL. */
  frameUrl?: string;
}

/**
 * Friendly Captcha for the web component, mounted inside a frame served from the Smart Widget origin.
 *
 * In web-component delivery the widget renders in the integrator's document, whose CSP decides what
 * may be framed. The SDK frames `*.frcapi.com`, which bank CSPs do not allow, while they do allow the
 * Smart Widget origin. So the SDK runs inside `frame.html` and reports back over postMessage.
 *
 * Mirrors the SDK widget the iframe embed mounts directly: solving starts on the first focus inside
 * the surrounding form (the SDK's default `focus` start mode) or when the customer clicks the widget.
 */
export class FriendlyCaptchaFrame {
  private readonly iframe: HTMLIFrameElement;
  private readonly frameOrigin: string;
  private readonly channel = createChannelId();
  private readonly form: HTMLFormElement | null;
  private frameReady = false;
  private startRequested = false;
  private destroyed = false;

  private readonly onMessage = (event: MessageEvent): void => {
    const data = event.data;
    if (
      this.destroyed ||
      event.source !== this.iframe.contentWindow ||
      event.origin !== this.frameOrigin ||
      !data || data.source !== FRIENDLY_CAPTCHA_FRAME_SOURCE || data.channel !== this.channel
    ) {
      return;
    }
    switch (data.type) {
      case 'ready':
        this.frameReady = true;
        if (this.startRequested) {
          this.postStart();
        }
        break;
      case 'complete':
        if (typeof data.response === 'string') {
          this.options.onComplete(data.response);
        }
        break;
      case 'error':
        this.options.onError(data.error);
        break;
    }
  };

  private readonly onFormFocus = (): void => this.start();

  constructor(private readonly options: FriendlyCaptchaFrameOptions) {
    const frameUrl = options.frameUrl ?? FRIENDLY_CAPTCHA_FRAME_URL;
    this.frameOrigin = new URL(frameUrl).origin;

    const params = new URLSearchParams({
      channel: this.channel,
      origin: window.location.origin,
      sitekey: options.sitekey,
      lang: options.language,
    });
    this.iframe = document.createElement('iframe');
    this.iframe.className = 'cbsw-friendly-captcha-frame';
    this.iframe.title = 'Friendly Captcha';
    this.iframe.src = `${frameUrl}#${params.toString()}`;

    window.addEventListener('message', this.onMessage);
    options.element.appendChild(this.iframe);

    this.form = options.element.closest('form');
    this.form?.addEventListener('focusin', this.onFormFocus, { once: true });
  }

  /** Starts solving in the background; delivered once the frame has loaded. */
  start(): void {
    if (this.destroyed || this.startRequested) {
      return;
    }
    this.startRequested = true;
    if (this.frameReady) {
      this.postStart();
    }
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    window.removeEventListener('message', this.onMessage);
    this.form?.removeEventListener('focusin', this.onFormFocus);
    this.iframe.remove();
  }

  private postStart(): void {
    this.iframe.contentWindow?.postMessage(
      { source: FRIENDLY_CAPTCHA_HOST_SOURCE, channel: this.channel, type: 'start' },
      this.frameOrigin
    );
  }
}

function createChannelId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}
